// Shared token-based search + ranking, used by the MultiSelect filter, the
// topics manager, and any other list/dropdown filter.
//
// Behavior (see product spec): the query is split into whitespace tokens and
// matching is ORDER-INDEPENDENT — an item matches only if it contains EVERY
// token (AND). Results are then ranked so the most "literal" matches float up:
// an exact contiguous phrase first, then tokens appearing in order, then a
// prefix match, then word-boundary hits, with shorter labels breaking ties.

function escapeRegex(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export function tokenize(query) {
  return String(query || '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
}

// Score one candidate `text` against `query`. Returns null when it doesn't match
// (a token is missing), otherwise a number where higher = better.
export function matchScore(query, text) {
  const q = String(query || '').trim().toLowerCase()
  if (!q) return 0 // neutral: no query means "keep as-is"

  const hay = String(text || '').toLowerCase()
  const tokens = q.split(/\s+/).filter(Boolean)

  // AND: every token must appear somewhere in the text.
  for (const token of tokens) {
    if (!hay.includes(token)) return null
  }

  let score = 0

  // 1. Exact phrase (the whole query, contiguous) — the strongest signal.
  const phraseIdx = hay.indexOf(q)
  if (phraseIdx !== -1) {
    score += 1000
    if (phraseIdx === 0) score += 250 // phrase at the very start
  }

  // 2. Tokens appear in order (as a subsequence, not necessarily adjacent).
  let cursor = -1
  let inOrder = true
  for (const token of tokens) {
    const at = hay.indexOf(token, cursor + 1)
    if (at === -1) {
      inOrder = false
      break
    }
    cursor = at
  }
  if (inOrder) score += 300

  // 3. Prefix: the label starts with the first token.
  if (tokens[0] && hay.startsWith(tokens[0])) score += 150

  // 4. Word-boundary hit per token (matches a whole word start).
  for (const token of tokens) {
    if (new RegExp('\\b' + escapeRegex(token)).test(hay)) score += 20
  }

  // 5. Tighter (shorter) labels rank above longer ones on ties.
  score += Math.max(0, 80 - hay.length)

  return score
}

// Filter + rank a list against a query. `getText(item)` yields the string to
// match. Empty query returns the list unchanged (original order preserved).
// Ties keep their original relative order (Array.prototype.sort is stable).
export function filterAndRank(items, query, getText) {
  const q = String(query || '').trim()
  if (!q) return items
  return items
    .map((item, index) => ({ item, index, score: matchScore(q, getText(item)) }))
    .filter((entry) => entry.score !== null)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((entry) => entry.item)
}
